import React from 'react';
import {
  ArrowForward,
  AssignmentTurnedIn,
  BookOutlined,
  Checklist,
  FamilyRestroom,
  School,
  Science,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const learningFeatures = [
  {
    title: 'WAEC and JAMB preparation',
    description: 'Explore subject topics, focused study materials, and practice questions for your exams.',
    icon: <School />,
  },
  {
    title: 'Practice and mock tests',
    description: 'Build confidence by practising exam-style questions and reviewing your progress.',
    icon: <Checklist />,
  },
  {
    title: 'Interactive practical labs',
    description: 'Use guided simulations to understand key science practicals and procedures.',
    icon: <Science />,
  },
  {
    title: 'Classroom resources',
    description: 'Access class materials, assignments, quizzes, and school work when your school provides an account.',
    icon: <BookOutlined />,
  },
  {
    title: 'Track your learning',
    description: 'Review your activity, results, attendance, and feedback through the tools available to your account.',
    icon: <AssignmentTurnedIn />,
  },
  {
    title: 'Support for families',
    description: 'Parents and guardians can use the family portal to follow school updates and get help when needed.',
    icon: <FamilyRestroom />,
  },
];

const HighSchoolAbout: React.FC = () => (
  <Box sx={{ py: { xs: 5, md: 9 }, backgroundColor: 'background.default' }}>
    <Container maxWidth="lg">
      <Box
        sx={{
          maxWidth: 800,
          mx: 'auto',
          mb: { xs: 5, md: 7 },
          textAlign: 'center',
        }}
      >
        <Typography color="secondary.main" variant="overline" sx={{ fontWeight: 800, letterSpacing: 1.5 }}>
          A3 High School
        </Typography>
        <Typography variant="h1" sx={{ mt: 1, fontSize: { xs: '2.2rem', md: '3.4rem' } }}>
          Learn with focus. Prepare with confidence.
        </Typography>
        <Typography color="text.secondary" variant="h6" sx={{ mt: 2, mx: 'auto', maxWidth: 700, lineHeight: 1.7 }}>
          A3 High School supports secondary-school learners with WAEC and JAMB preparation, interactive practicals, mock tests, and school-provided learning tools.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
          <Button component={RouterLink} to="/waec" variant="contained" endIcon={<ArrowForward />}>
            Explore WAEC topics
          </Button>
          <Button component={RouterLink} to="/high-school/contact" variant="outlined">
            Contact student support
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={2.5}>
        {learningFeatures.map((feature) => (
          <Grid item xs={12} sm={6} md={4} key={feature.title}>
            <Card sx={{ height: '100%', backgroundColor: 'background.paper' }}>
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 44,
                    height: 44,
                    mb: 2,
                    borderRadius: 2,
                    color: 'secondary.main',
                    backgroundColor: 'action.hover',
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography variant="h5" sx={{ mb: 1 }}>
                  {feature.title}
                </Typography>
                <Typography color="text.secondary" variant="body2" sx={{ lineHeight: 1.7 }}>
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3} sx={{ mt: { xs: 3, md: 5 } }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', backgroundColor: 'background.paper' }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Typography variant="h4" sx={{ mb: 1.5 }}>
                Learning built around your goals
              </Typography>
              <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Choose WAEC or JAMB topics to revise at your own pace, practise with exam-style questions, and use guided science simulations to strengthen your understanding. Available subjects and materials depend on the resources currently provided on the platform.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', backgroundColor: 'background.paper' }}>
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Typography variant="h4" sx={{ mb: 1.5 }}>
                Learning with your school
              </Typography>
              <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                If your school has enabled school access, sign in with the account provided to view class materials, assignments, quizzes, results, and other school updates. Parents and guardians can sign in separately to access the family services available to them.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: { xs: 5, md: 7 },
          p: { xs: 3, md: 4 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          backgroundColor: 'background.paper',
          textAlign: 'center',
        }}
      >
        <Typography variant="h4" sx={{ mb: 1 }}>
          Get started or get support
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2.5 }}>
          Explore exam preparation, continue with your school work, or contact the student support team for help.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
          <Button component={RouterLink} to="/jamb" variant="contained" endIcon={<ArrowForward />}>
            Explore JAMB topics
          </Button>
          <Button component={RouterLink} to="/school-work" variant="outlined">
            Go to School Work
          </Button>
          <Button component={RouterLink} to="/high-school/contact" variant="outlined">
            Student support
          </Button>
        </Stack>
      </Box>
    </Container>
  </Box>
);

export default HighSchoolAbout;
